import { alpha, createTheme } from "@mui/material/styles";

/**
 * Single source of truth for typography across the whole app.
 * Import this anywhere you need to match the global type scale in sx props:
 *   import { typographyScale } from "../styles/theme";
 *   sx={{ fontSize: typographyScale.fontSize.md }}
 */
export const typographyScale = {
  fontSize: {
    xs:   "0.6875rem",  // 11px — captions, overlines, table col headers
    sm:   "0.75rem",    // 12px — body2, secondary text, chips
    base: "0.8125rem",  // 13px — body1, primary reading text
    md:   "0.875rem",   // 14px — h6, prominent labels, tab names
    lg:   "1rem",       // 16px — h5, section headings
    xl:   "1.125rem",   // 18px — h4 / h3, sub-page headings
    "2xl":"1.5rem",     // 24px — h1 / h2, page titles
  },
  fontWeight: {
    regular:  400,
    medium:   500,
    semibold: 600,
    bold:     700,
  },
  lineHeight: {
    tight:  1.25,
    normal: 1.5,
  },
};

const engineeringTokens = {
  light: {
    bg: { default: "#eef1f6", paper: "#ffffff", elevated: "#f8f9fc" },
    border: { subtle: "#d8dee9", strong: "#b8c2d0" },
    accent: {
      brand: "#d946ef",
      cyan: "#fde047",
      teal: "#890089",
      tealHover: "#d946ef",
    },
    text: { primary: "#1a2332", secondary: "#5a6478" },
  },
  dark: {
    bg: { default: "#0c1017", paper: "#141a24", elevated: "#1a2230" },
    border: { subtle: "#2a3444", strong: "#3d4f66" },
    accent: {
      brand: "rgb(165, 0, 165)",
      cyan: "#fde047",
      teal: "#d300d3",
      tealHover: "#d946ef",
    },
    text: { primary: "#e8edf4", secondary: "#adb9cc" },
  },
};

const scrollbarStyles = (mode) => {
  const track = mode === "dark" ? "#141a24" : "#eef1f6";
  const thumb = mode === "dark" ? "#3d4f66" : "#b8c2d0";
  const thumbHover = mode === "dark" ? "#22d3ee" : "#4043ac";

  return {
    scrollbarWidth: "thin",
    scrollbarColor: `${thumb} ${track}`,
    "&::-webkit-scrollbar": { width: 10, height: 10 },
    "&::-webkit-scrollbar-track": { backgroundColor: track },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: thumb,
      borderRadius: 6,
      border: `2px solid ${track}`,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: thumbHover,
    },
  };
};

export const createAppTheme = (mode = "light") => {
  const isDark = mode === "dark";
  const tokens = isDark ? engineeringTokens.dark : engineeringTokens.light;

  return createTheme({
    palette: {
      mode,
      primary: {
        main: tokens.accent.teal,
        light: isDark ? "#d300d3" : "#890089",
        dark: isDark ? tokens.accent.tealHover : tokens.accent.tealHover,
        contrastText: "#ffffff",
      },
      secondary: {
        main: tokens.accent.brand,
        light: isDark ? "#a5b4fc" : "#7a8bd0",
        dark: isDark ? "#6366f1" : "#3d4f8f",
        contrastText: "#ffffff",
      },
      background: {
        default: tokens.bg.default,
        paper: tokens.bg.paper,
      },
      text: {
        primary: tokens.text.primary,
        secondary: tokens.text.secondary,
      },
      divider: tokens.border.subtle,
      success: {
        main: isDark ? "#34d399" : "#059669",
      },
      warning: {
        main: isDark ? "#fbbf24" : "#F59E0B",
        contrastText: isDark ? "#0c1017" : "#FFFFFF",
      },
      error: {
        main: isDark ? "#DC2626" : "#DC2626",
      },
      info: {
        main: isDark ? "#60a5fa" : "#3B82F6",
      },
      action: {
        hover: alpha(tokens.accent.cyan, isDark ? 0.08 : 0.06),
        selected: alpha(tokens.accent.cyan, isDark ? 0.14 : 0.1),
      },
    },
    typography: {
      fontFamily: '"Open Sans", "Segoe UI", Roboto, sans-serif',
      htmlFontSize: 16,
      h1: { fontSize: typographyScale.fontSize["2xl"], fontWeight: typographyScale.fontWeight.bold,     lineHeight: typographyScale.lineHeight.tight,  letterSpacing: "-0.02em" },
      h2: { fontSize: typographyScale.fontSize["2xl"], fontWeight: typographyScale.fontWeight.bold,     lineHeight: typographyScale.lineHeight.tight,  letterSpacing: "-0.02em" },
      h3: { fontSize: typographyScale.fontSize.xl,     fontWeight: typographyScale.fontWeight.semibold, lineHeight: typographyScale.lineHeight.tight,  letterSpacing: "-0.01em" },
      h4: { fontSize: typographyScale.fontSize.xl,     fontWeight: typographyScale.fontWeight.semibold, lineHeight: typographyScale.lineHeight.tight },
      h5: { fontSize: typographyScale.fontSize.lg,     fontWeight: typographyScale.fontWeight.semibold, lineHeight: typographyScale.lineHeight.normal },
      h6: { fontSize: typographyScale.fontSize.md,     fontWeight: typographyScale.fontWeight.semibold, lineHeight: typographyScale.lineHeight.normal },
      subtitle1: { fontSize: typographyScale.fontSize.md,   fontWeight: typographyScale.fontWeight.medium,   lineHeight: typographyScale.lineHeight.normal, letterSpacing: "0.01em" },
      subtitle2: { fontSize: typographyScale.fontSize.xs,   fontWeight: typographyScale.fontWeight.semibold, lineHeight: typographyScale.lineHeight.normal, letterSpacing: "0.08em", textTransform: "uppercase" },
      body1:     { fontSize: typographyScale.fontSize.base, fontWeight: typographyScale.fontWeight.regular,  lineHeight: typographyScale.lineHeight.normal },
      body2:     { fontSize: typographyScale.fontSize.sm,   fontWeight: typographyScale.fontWeight.regular,  lineHeight: typographyScale.lineHeight.normal },
      button:    { fontSize: typographyScale.fontSize.sm,   fontWeight: typographyScale.fontWeight.semibold, letterSpacing: "0.02em", textTransform: "none" },
      caption:   { fontSize: typographyScale.fontSize.xs,   fontWeight: typographyScale.fontWeight.regular,  lineHeight: typographyScale.lineHeight.normal, letterSpacing: "0.04em", fontFamily: '"Roboto Mono", "JetBrains Mono", monospace' },
      overline:  { fontSize: typographyScale.fontSize.xs,   fontWeight: typographyScale.fontWeight.semibold, lineHeight: typographyScale.lineHeight.normal, letterSpacing: "0.08em", fontFamily: '"Roboto Mono", "JetBrains Mono", monospace' },
    },
    shape: {
      borderRadius: 6,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            colorScheme: mode,
            // PV data tables (Shell / Nozzle) paint their own header and
            // border colours through these, so a table that builds plain
            // style objects - no theme in hand - still follows the theme.
            "--pv-table-header-bg": isDark
              ? tokens.bg.elevated
              : "hsla(250, 86%, 97%, 1)",
            "--pv-table-header-text": tokens.text.primary,
            "--pv-table-border": isDark
              ? tokens.border.subtle
              : "rgba(170, 180, 198, 0.6)",
          },
          body: {
            backgroundColor: tokens.bg.default,
            color: tokens.text.primary,
            ...scrollbarStyles(mode),
          },
          "#wrapper.app": {
            backgroundColor: tokens.bg.default,
            color: tokens.text.primary,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: tokens.bg.paper,
            color: tokens.text.primary,
            boxShadow: isDark ? "none" : "0 1px 3px rgba(26, 35, 50, 0.08)",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: isDark ? tokens.bg.elevated : tokens.bg.paper,
            borderBottom: `1px solid ${tokens.border.subtle}`,
            boxShadow: "none",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: tokens.bg.elevated,
            borderRight: `1px solid ${tokens.border.subtle}`,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? tokens.bg.elevated : tokens.bg.default,
            "& .MuiTableCell-head": {
              backgroundColor: isDark ? tokens.bg.elevated : tokens.bg.default,
              color: tokens.text.primary,
              fontWeight: 600,
              borderColor: tokens.border.subtle,
            },
          },
        },
      },
      MuiTableFooter: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.bg.paper,
            color: tokens.text.primary,
            "& .MuiTableCell-footer": {
              backgroundColor: tokens.bg.paper,
              color: tokens.text.primary,
              borderColor: tokens.border.subtle,
            },
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.bg.paper,
            color: tokens.text.primary,
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.bg.paper,
            color: tokens.text.primary,
          },
          toolbar: {
            backgroundColor: tokens.bg.paper,
            color: tokens.text.primary,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: tokens.border.subtle,
            color: tokens.text.primary,
          },
          head: {
            fontWeight: 600,
            backgroundColor: isDark ? tokens.bg.elevated : tokens.bg.default,
            color: tokens.text.primary,
          },
          body: {
            backgroundColor: "inherit",
          },
          footer: {
            backgroundColor: tokens.bg.paper,
            color: tokens.text.primary,
          },
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.bg.paper,
            "& .MuiTableRow-root": {
              backgroundColor: tokens.bg.paper,
            },
            "& .MuiTableCell-body": {
              backgroundColor: "inherit",
              color: tokens.text.primary,
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.bg.paper,
            "&.Mui-selected": {
              backgroundColor: alpha(tokens.accent.cyan, isDark ? 0.12 : 0.08),
            },
            "&.Mui-selected:hover": {
              backgroundColor: alpha(tokens.accent.cyan, isDark ? 0.18 : 0.14),
            },
          },
        },
      },
      MuiButton: {
        variants: [
          {
            props: { variant: "outlined-indigo" },
            style: {
              backgroundColor: tokens.bg.paper,
              border: `1px solid #6366f1`,
              color: "#6366f1",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 6,
              "&:hover": {
                backgroundColor: isDark
                  ? "rgba(99, 102, 241, 0.12)"
                  : "rgba(99, 102, 241, 0.06)",
                borderColor: "#818cf8",
                color: "#818cf8",
              },
            },
          },
        ],
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 6,
          },
          containedPrimary: {
            backgroundColor: tokens.accent.teal,
            color: "#ffffff",
            "&:hover": {
              backgroundColor: tokens.accent.tealHover,
            },
          },
          containedSecondary: {
            backgroundColor: tokens.accent.brand,
            "&:hover": {
              backgroundColor: isDark ? "#6366f1" : "#3d4f8f",
            },
          },
          outlinedPrimary: {
            borderColor: alpha(tokens.accent.teal, 0.6),
            color: tokens.accent.teal,
            "&:hover": {
              borderColor: tokens.accent.teal,
              backgroundColor: alpha(tokens.accent.teal, isDark ? 0.1 : 0.06),
            },
          },
          outlinedSecondary: {
            borderColor: alpha(tokens.accent.brand, 0.55),
            color: tokens.accent.brand,
            "&:hover": {
              borderColor: tokens.accent.brand,
              backgroundColor: alpha(tokens.accent.brand, isDark ? 0.1 : 0.06),
            },
          },
          textPrimary: {
            color: tokens.accent.teal,
          },
          textSecondary: {
            color: tokens.accent.brand,
          },
          outlined: {
            borderColor: tokens.border.strong,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? tokens.bg.elevated : tokens.bg.paper,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: tokens.border.subtle,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: tokens.border.strong,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: tokens.accent.teal,
              borderWidth: 1,
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: tokens.bg.paper,
            border: `1px solid ${tokens.border.strong}`,
            boxShadow: isDark
              ? `0 24px 48px ${alpha("#000000", 0.5)}`
              : undefined,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 40,
            borderBottom: `1px solid ${tokens.border.subtle}`,
          },
          indicator: {
            height: 3,
            borderRadius: "3px 3px 0 0",
            backgroundColor: tokens.text.primary,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 40,
            fontWeight: 600,
            letterSpacing: "0.02em",
            "&.Mui-selected": {
              color: tokens.text.primary,
              fontWeight: 700,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontFamily: '"Roboto Mono", monospace',
            fontSize: "0.7rem",
            borderRadius: 4,
          },
          outlined: {
            borderColor: tokens.border.strong,
          },
          colorInfo: isDark
            ? {
                backgroundColor: alpha("#60a5fa", 0.18),
                color: "#bfdbfe",
                border: `1px solid ${alpha("#60a5fa", 0.5)}`,
              }
            : {},
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? tokens.bg.elevated : "#1a2332",
            border: `1px solid ${tokens.border.strong}`,
            fontSize: "0.75rem",
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: tokens.border.subtle,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            border: `1px solid ${tokens.border.subtle}`,
          },
        },
      },
    },
  });
};

/** @deprecated Prefer createAppTheme(colorMode) via AppThemeProvider */
export const theme = createAppTheme("light");
